/**
 * ============================================================================
 * productionConfigService.js - Frontend Service
 * ============================================================================
 *
 * Serviço para comunicação com API de fórmulas de produção
 *
 * Endpoints:
 * - GET    /admin/production-formulas
 * - POST   /admin/production-formulas
 * - PUT    /admin/production-formulas/:id
 * - DELETE /admin/production-formulas/:id
 * - POST   /admin/production-formulas/:id/activate
 * - POST   /admin/production-formulas/test
 * - POST   /admin/production-formulas/validate
 * - GET    /admin/production-formulas/:id/logs
 *
 * ============================================================================
 */

import axios from "axios";
import { supabase } from "./supabaseClient";

/**
 * Obter URL da API
 * Tenta: window.ENV.REACT_APP_API_URL → process.env.REACT_APP_API_URL → "/api"
 */
const getApiUrl = () => {
  return (
    (typeof window !== "undefined" && window.ENV?.REACT_APP_API_URL) ||
    process.env.REACT_APP_API_URL ||
    "/api"
  );
};

const API_URL = getApiUrl();

/**
 * Criar instância axios com configurações padrão
 */
const axiosInstance = axios.create({
  baseURL: API_URL,
});

/**
 * Interceptor para adicionar token de autenticação
 */
axiosInstance.interceptors.request.use(
  async (config) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error("Erro ao obter token de autenticação:", error);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ============================================================================
// SERVICE OBJECT
// ============================================================================

export const productionConfigService = {
  /**
   * Busca todas as fórmulas
   * GET /admin/production-formulas
   * @returns {Promise<Array>}
   */
  async getFormulas() {
    try {
      const response = await axiosInstance.get("/admin/production-formulas");
      return response.data.data || [];
    } catch (error) {
      throw this._handleError(error, "Erro ao buscar fórmulas");
    }
  },

  /**
   * Busca a fórmula ativa atualmente
   * GET /admin/production-formulas/active
   * @returns {Promise<Object>}
   */
  async getActiveFormula() {
    try {
      const response = await axiosInstance.get(
        "/admin/production-formulas/active"
      );
      return response.data.data;
    } catch (error) {
      throw this._handleError(error, "Erro ao buscar fórmula ativa");
    }
  },

  /**
   * Busca uma fórmula pelo ID
   * GET /admin/production-formulas/:id (não implementado no backend, mas pode ser útil)
   * @param {number} id
   * @returns {Promise<Object>}
   */
  async getFormulaById(id) {
    try {
      const response = await axiosInstance.get(
        `/admin/production-formulas/${id}`
      );
      return response.data.data;
    } catch (error) {
      throw this._handleError(error, "Erro ao buscar fórmula");
    }
  },

  /**
   * Cria uma nova fórmula
   * POST /admin/production-formulas
   * @param {Object} formulaData - { name, description, formula, is_active }
   * @returns {Promise<Object>}
   */
  async createFormula(formulaData) {
    try {
      if (!formulaData.name || !formulaData.formula) {
        throw new Error("Nome e fórmula são obrigatórios");
      }

      const response = await axiosInstance.post(
        "/admin/production-formulas",
        formulaData
      );
      return response.data.data;
    } catch (error) {
      throw this._handleError(error, "Erro ao criar fórmula");
    }
  },

  /**
   * Atualiza uma fórmula
   * PUT /admin/production-formulas/:id
   * @param {number} id
   * @param {Object} formulaData
   * @returns {Promise<Object>}
   */
  async updateFormula(id, formulaData) {
    try {
      const response = await axiosInstance.put(
        `/admin/production-formulas/${id}`,
        formulaData
      );
      return response.data.data;
    } catch (error) {
      throw this._handleError(error, "Erro ao atualizar fórmula");
    }
  },

  /**
   * Deleta uma fórmula
   * DELETE /admin/production-formulas/:id
   * @param {number} id
   * @returns {Promise<void>}
   */
  async deleteFormula(id) {
    try {
      await axiosInstance.delete(`/admin/production-formulas/${id}`);
    } catch (error) {
      throw this._handleError(error, "Erro ao deletar fórmula");
    }
  },

  /**
   * Ativa uma fórmula
   * POST /admin/production-formulas/:id/activate
   * @param {number} id
   * @returns {Promise<Object>}
   */
  async activateFormula(id) {
    try {
      const response = await axiosInstance.post(
        `/admin/production-formulas/${id}/activate`
      );
      return response.data.data;
    } catch (error) {
      throw this._handleError(error, "Erro ao ativar fórmula");
    }
  },

  /**
   * Desativa uma fórmula
   * POST /admin/production-formulas/:id/deactivate
   * @param {number} id
   * @returns {Promise<Object>}
   */
  async deactivateFormula(id) {
    try {
      const response = await axiosInstance.post(
        `/admin/production-formulas/${id}/deactivate`
      );
      return response.data.data;
    } catch (error) {
      throw this._handleError(error, "Erro ao desativar fórmula");
    }
  },

  /**
   * Testa uma fórmula
   * POST /admin/production-formulas/test
   * @param {string} formula
   * @param {number} productId - Opcional
   * @returns {Promise<Object>} - { success, result, sqlGenerated, productUsed, error }
   */
  async testFormula(formula, productId = null) {
    try {
      if (!formula) {
        throw new Error("Fórmula é obrigatória para teste");
      }

      const response = await axiosInstance.post(
        "/admin/production-formulas/test",
        {
          formula,
          product_id: productId,
        }
      );
      return response.data.data;
    } catch (error) {
      // Retornar erro de forma mais amigável
      if (error.response?.data?.data) {
        return error.response.data.data;
      }
      throw this._handleError(error, "Erro ao testar fórmula");
    }
  },

  /**
   * Executa uma fórmula para um produto real, sem salvar alterações, e
   * retorna o resultado, as variáveis avaliadas e o SQL gerado.
   */
  async debugFormula(formula, productId) {
    try {
      const response = await axiosInstance.post('/admin/production-formulas/debug', {
        formula,
        product_id: productId,
      });
      return response.data.data;
    } catch (error) {
      throw this._handleError(error, 'Erro ao diagnosticar fórmula');
    }
  },

  /**
   * Valida a sintaxe de uma fórmula
   * POST /admin/production-formulas/validate
   * @param {string} formula
   * @returns {Promise<Object>} - { valid, message }
   */
  async validateFormula(formula) {
    try {
      if (!formula) {
        throw new Error("Fórmula é obrigatória");
      }

      const response = await axiosInstance.post(
        "/admin/production-formulas/validate",
        {
          formula,
        }
      );
      return response.data;
    } catch (error) {
      if (error.response?.data) {
        return error.response.data;
      }
      throw this._handleError(error, "Erro na validação");
    }
  },

  /**
   * Busca o histórico de uma fórmula
   * GET /admin/production-formulas/:id/logs
   * @param {number} id
   * @returns {Promise<Array>}
   */
  async getFormulaLogs(id) {
    try {
      const response = await axiosInstance.get(
        `/admin/production-formulas/${id}/logs`
      );
      return response.data.data || [];
    } catch (error) {
      throw this._handleError(error, "Erro ao buscar logs");
    }
  },

  /**
   * Obter estatísticas do cache
   * GET /admin/cache-stats
   * @returns {Promise<Object>}
   */
  async getCacheStats() {
    try {
      const response = await axiosInstance.get("/admin/cache-stats");
      return response.data.data;
    } catch (error) {
      throw this._handleError(error, "Erro ao obter estatísticas de cache");
    }
  },

  /**
   * Limpar cache (desenvolvimento)
   * POST /admin/cache-clear
   * @returns {Promise<void>}
   */
  async clearCache() {
    try {
      await axiosInstance.post("/admin/cache-clear");
    } catch (error) {
      throw this._handleError(error, "Erro ao limpar cache");
    }
  },

  /**
   * Busca todos os produtos
   * GET /products
   * @returns {Promise<Array>}
   */
  async getProducts() {
    try {
      const response = await axiosInstance.get("/products");
      // A resposta retorna direto o array de produtos
      return Array.isArray(response.data) ? response.data : response.data.data || [];
    } catch (error) {
      console.warn("Erro ao buscar produtos:", error);
      return [];
    }
  },

  /**
   * Tratar erros de forma centralizada
   * @private
   */
  _handleError(error, defaultMessage) {
    if (error.response) {
      // Erro da API
      const apiError = error.response.data?.message || defaultMessage;
      return new Error(apiError);
    } else if (error.request) {
      // Erro de rede
      return new Error("Erro de conectividade. Verifique sua conexão.");
    } else {
      // Outro erro
      return new Error(error.message || defaultMessage);
    }
  },
};

export default productionConfigService;
